<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_overview_title')"
      :subtitle="$t('ga_admin_overview_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-progress-head">
          <div class="loading-progress-step">
            <span>{{ $t('ga_admin_loading_step', {step: loadingProgress.currentLabel || $t('ga_admin_loading_overview')}) }}</span>
            <small>{{ $t('ga_admin_loading_position', {current: loadingCurrentModuleIndex, total: loadingProgress.totalModules}) }}</small>
          </div>
          <div class="loading-progress-percent">{{ loadingProgress.percent }}%</div>
        </div>
        <div class="loading-progress-track">
          <div class="loading-progress-fill" :style="{width: `${loadingProgress.percent}%`}"></div>
        </div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_overall') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_overall_desc') }}</div>
            </div>
          </div>
          <div class="stats-grid">
            <div v-for="item in overviewStats" :key="item.title" class="stat-card">
              <div class="stat-title">{{ item.title }}</div>
              <div class="stat-value">{{ item.value }}</div>
              <div :class="['stat-change', item.tone]">{{ item.change }}</div>
              <div class="stat-desc">{{ item.desc }}</div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_ai_daily') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_ai_daily_desc') }}</div>
            </div>
            <button class="ghost-btn ai-action-btn" @click="openAi($t('ga_admin_ai_prompt_daily'))">
              <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
              <span>{{ $t('ga_admin_further_analysis') }}</span>
            </button>
          </div>
          <div class="summary-list">
            <div v-for="item in aiHighlights" :key="item" class="summary-item">
              <span class="summary-dot"></span>
              <span>{{ item }}</span>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_feedback_overview') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_feedback_overview_desc') }}</div>
            </div>
          </div>
          <div class="board-grid">
            <div v-for="board in mindsetBoards" :key="board.title" class="board-card">
              <div class="board-title">{{ board.title }}</div>
              <div class="board-subtitle">{{ board.subtitle }}</div>
              <div class="board-summary">{{ board.summary }}</div>
              <div class="metric-row">
                <div v-for="metric in board.metrics" :key="metric.label" class="metric-chip">
                  <span class="metric-label">{{ metric.label }}</span>
                  <span class="metric-value">{{ metric.value }}</span>
                </div>
              </div>
              <div class="tag-row">
                <span v-for="tag in board.tags" :key="tag" class="soft-tag">{{ tag }}</span>
                <span v-if="!board.tags.length" class="soft-tag empty">{{ $t('ga_admin_empty_feedback_tags') }}</span>
              </div>
            </div>
          </div>
          <div class="rule-note">
            <div v-for="rule in positiveFeedbackRules" :key="rule" class="rule-row">{{ rule }}</div>
          </div>
        </section>
      </template>

    </div>
  </div>
</template>

<script>
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';

export default {
  name: 'GAAdminOverview',
  components: {
    AdminHeader,
  },
  data() {
    return {
      loading: false,
      loadingProgress: {
        completedModules: 0,
        currentLabel: '',
        percent: 0,
        totalModules: 4,
      },
      errorMessage: '',
      analytics: {
        overviewStats: [],
        aiHighlights: [],
        mindsetBoards: [],
        positiveFeedbackRules: [],
      },
    };
  },
  computed: {
    overviewStats() {
      return this.analytics.overviewStats;
    },
    aiHighlights() {
      return this.analytics.aiHighlights;
    },
    mindsetBoards() {
      return this.analytics.mindsetBoards;
    },
    positiveFeedbackRules() {
      return this.analytics.positiveFeedbackRules;
    },
    loadingCurrentModuleIndex() {
      const total = Math.max(Number(this.loadingProgress.totalModules) || 1, 1);
      return Math.min((Number(this.loadingProgress.completedModules) || 0) + 1, total);
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      this.resetLoadingProgress(this.$t('ga_admin_loading_overview_source'));
      try {
        const analytics = await loadGaAdminAnalytics({
          onProgress: this.updateLoadingProgress,
        });
        this.analytics = {
          ...this.analytics,
          ...analytics,
        };
        this.updateLoadingProgress({
          completedModules: this.loadingProgress.totalModules,
          currentLabel: this.$t('ga_admin_loading_overview_dashboard'),
          percent: 100,
          totalModules: this.loadingProgress.totalModules,
        });
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_overview');
      } finally {
        this.loading = false;
      }
    },
    resetLoadingProgress(currentLabel) {
      this.loadingProgress = {
        completedModules: 0,
        currentLabel,
        percent: 0,
        totalModules: 4,
      };
    },
    updateLoadingProgress({
      completedModules = 0,
      currentLabel = '',
      labelKey = '',
      percent,
      totalModules = 4,
    } = {}) {
      const total = Math.max(Number(totalModules) || 1, 1);
      const completed = Math.min(Number(completedModules) || 0, total);
      this.loadingProgress = {
        completedModules: completed,
        currentLabel: currentLabel || (labelKey ? this.$t(labelKey) : this.loadingProgress.currentLabel),
        percent: percent === undefined ? Math.min(99, Math.round((completed / total) * 100)) : percent,
        totalModules: total,
      };
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #031b13;
  background:
    linear-gradient(122deg, transparent 0 16%, rgba(18, 83, 53, 0.56) 16% 29%, transparent 29% 100%),
    linear-gradient(122deg, transparent 0 60%, rgba(10, 58, 38, 0.72) 60% 76%, transparent 76% 100%),
    linear-gradient(180deg, #064527 0%, #003d25 34%, #031b13 100%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #00b852;
}

.loading-card {
  min-height: 240px;
  display: flex;
  width: min(260px, 76vw);
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-progress-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.loading-progress-step {
  min-width: 0;
  color: #596579;
  font-size: 11px;
  line-height: 16px;
  text-align: left;
}

.loading-progress-step span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading-progress-step small {
  display: block;
  margin-top: 1px;
  color: #8b95a5;
  font-size: 10px;
  line-height: 14px;
}

.loading-progress-percent {
  flex-shrink: 0;
  color: #00b852;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
}

.loading-progress-track {
  width: 100%;
  height: 5px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #f0e2dd;
}

.loading-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #00b852 0%, #17c86b 100%);
  transition: width 0.25s ease;
}

.stats-grid,
.board-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.section-card,
.stat-card,
.board-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.ghost-btn {
  flex-shrink: 0;
  border: 1.5px solid #00b852;
  border-radius: 999px;
  padding: 8px 12px;
  background: #fff;
  color: #00b852;
  font-size: 12px;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #006b4f 0%, #008f72 48%, #00b852 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(0, 184, 82, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

.stat-card {
  padding: 14px;
  background: linear-gradient(180deg, #fff, #f8fbf9);
}

.stat-title {
  font-size: 12px;
  color: #666;
}

.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.stat-change {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
}

.stat-change.positive {
  color: #0e9f6e;
}

.stat-change.neutral {
  color: #1f3c88;
}

.stat-change.warning {
  color: #00b852;
}

.stat-desc {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.summary-list {
  display: grid;
  gap: 10px;
}

.summary-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #f2f5f9;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
}

.summary-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: #00b852;
}

.board-card {
  padding: 14px;
  background: #fff;
}

.hero-grid {
  margin-bottom: 12px;
}

.board-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.board-subtitle {
  margin-top: 4px;
  font-size: 11px;
  color: #999;
}

.board-summary {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.metric-row,
.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.metric-chip {
  min-width: 88px;
  padding: 10px;
  border-radius: 12px;
  background: #f2f5f9;
}

.metric-label {
  display: block;
  font-size: 11px;
  color: #999;
}

.metric-value {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.soft-tag {
  padding: 6px 10px;
  border-radius: 999px;
  background: #e5f8ed;
  font-size: 11px;
  color: #00b852;
}

.soft-tag.empty {
  background: #f8fbf9;
  color: #999;
}

.rule-note {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #f8fbf9;
}

.rule-row {
  font-size: 11px;
  line-height: 1.6;
  color: #7a4d42;
}

.rule-row + .rule-row {
  margin-top: 6px;
}

@media (max-width: 360px) {
  .stats-grid,
  .board-grid {
    grid-template-columns: 1fr;
  }
}
</style>
